import { useParams, useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { Button, LinearProgress } from "@mui/material";
import './ClientDetailsCalls.css';
import UploadTranscript from "../TranscriptButton/Transcript";
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import RadialSeparatorsCalls from './RadialSeparatosCalls';
import { API_URL } from "../../utils/auth";
import axios from 'axios';
import { jsPDF } from "jspdf";
import { formatInTimeZone } from 'date-fns-tz';
import html2canvas from 'html2canvas';
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField} from '@mui/material';
import Auth from '../../utils/auth';

const ClientDetailsCalls = ({
  setTranscript,
  score,
  sendTranscript,
  loading,
  saveCallScore,
  callScoreAvailable,
  setTranscriptId,
  isSavedSuccessfully,
  errorMessage,
  progress,
  callScores, 
  redFlags,
  greenFlags,
  callSummary,
  createdAt, 
}) => {
  const navigate = useNavigate();
  const { clientId, callScoreId } = useParams();
  const [isUploaded, setIsUploaded] = useState(false);
  const [showUploadButton, setShowUploadButton] = useState(true);
  const [scoreCallClicked, setScoreCallClicked] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showSaveButtonOnly, setShowSaveButtonOnly] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [buttonText, setButtonText] = useState("Score Call");
  const [isScoreCallDisabled, setIsScoreCallDisabled] = useState(false);
  const [isExportToPDFEnabled, setIsExportToPDFEnabled] = useState(false); 
  const user = Auth.getUser();
  const userId = user?.id;
  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);
  const [companyName, setCompanyName] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const selectedClientId = clientId;

  useEffect(() => {
    if (callScoreId) {
      setIsViewMode(true);
      setIsExportToPDFEnabled(true); 
    } else {
      setIsViewMode(false);
      setIsExportToPDFEnabled(false); 
    }
  }, [callScoreId]);

  const fetchClientData = async () => {
    if (!selectedClientId) return;
    try {
      const workspaceId = Auth.getWorkspaceId();
      if (!workspaceId) {
        throw new Error("Workspace ID is missing");
      }

      const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
        params: {
          clientId: selectedClientId,
          workspaceId: workspaceId,
          includeCompany: true
        }
      });
      
      const clientData = response.data;
      
      setClient(clientData);
      setCompanyName(clientData.companyName || null); 
      
      setLoadingClient(false);
    } catch (error) {
      console.error("Error fetching client data:", error);
      setLoadingClient(false);
      if (error.response?.status === 404) {
        navigate('/clients');
      }
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [selectedClientId]);

  const handleDeleteCallReview = async () => {
    try {
      const workspaceId = Auth.getWorkspaceId(); 
      if (!workspaceId) {
        throw new Error("Workspace ID is missing");
      }
      
      await axios.delete(
        `${API_URL}/call-score/delete-call-score`,
        {
          params: {
            id: callScoreId,
            workspaceId: workspaceId
          }
        }
      );
      navigate(`/client/${clientId}`);
    } catch (error) {
      console.error("Error deleting call review:", error);
      alert(error.response?.data?.message || "Failed to delete call review.");
    }
  };

  const calculateColorCalls = (value) => {
    if (value >= 7) return { label: 'Great', color: '#03BC72' };
    if (value >= 5) return { label: 'Good', color: '#F6A637' };
    return { label: 'At Risk', color: '#F52B53' };
  };

  const adjustedScore = (() => {
    if (score >= 10) return 10;
    if (score >= 7.5) return 7.5;
    if (score >= 5) return 5;
    if (score >= 2.5) return 2.5;
    return 0;
  })();

  const getStatus = (score) => {
    if (score >= 0 && score <= 3.99)
      return { color: "at-risk", label: "At Risk" };
    if (score >= 4.0 && score <= 6.99) return { color: "fair", label: "Fair" };
    if (score >= 7.0 && score <= 10.0) return { color: "good", label: "Good" };
    return { color: "unknown", label: "Unknown" };
  };

  const retentionData = calculateColorCalls(adjustedScore);

  useEffect(() => {
    if (!loading && scoreCallClicked) {
      setButtonText("Successful Score Call!");
      setIsScoreCallDisabled(true);
      setTimeout(() => {
        setShowProgressBar(false);
        setShowSaveButtonOnly(true);
      }, 3000);
    }
  }, [loading, scoreCallClicked]);

  const exportToPDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
    });

    const chartElement = document.getElementById("call-score-chart");
    let imgData = null;
    if (chartElement) {
      try {
        const canvas = await html2canvas(chartElement, {
          scale: 2, 
        });
        imgData = canvas.toDataURL("image/png");
      } catch (error) {
        console.error("Error capturing chart as image:", error);
      }
    }
  
    const preprocessText = (text) => {
      return text.replace(/_/g, ' ');
    };
  
    const formatDateWithTime = (date) => {
      if (!date) return "N/A";
  
      const timeZone = "America/New_York";
  
      const formattedDate = formatInTimeZone(date, timeZone, "MMM. d yyyy, h:mm a");
  
      return formattedDate;
    };
  
    const margin = 0.7;
    const spaceBetweenColumns = 0.4;
    const colWidth = (doc.internal.pageSize.width - 2 * margin - spaceBetweenColumns) / 2;
    const yOffsetStart = margin;
    let yOffset = yOffsetStart;
    let yOffsetCol2 = yOffsetStart;
    let inFirstColumn = true;
  
    doc.setFont("times", "normal");
    doc.setFontSize(8);
  
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
  
    const drawBorder = () => {
      doc.setLineWidth(0.02);
      doc.rect(0.3, 0.3, pageWidth - 0.6, pageHeight - 0.6);
    };
  
    const addSection = (title, content) => {
      const lineHeight = 0.14;
      const sectionSpacing = 0.05;
      const lines = doc.splitTextToSize(preprocessText(content), colWidth);
      const contentHeight = lines.length * lineHeight;
  
      if (inFirstColumn && (yOffset + contentHeight > pageHeight - margin)) {
        inFirstColumn = false;
      }
  
      let xPos = inFirstColumn ? margin : margin + colWidth + spaceBetweenColumns;
      let yPos = inFirstColumn ? yOffset : yOffsetCol2;
  
      if (!inFirstColumn && (yOffsetCol2 + contentHeight > pageHeight - margin)) {
        doc.addPage();
        drawBorder();
        yOffset = margin;
        yOffsetCol2 = margin;
        inFirstColumn = true;
        xPos = margin;
        yPos = yOffset;
      }
  
      doc.setFont("times", "bold");
      doc.text(title, xPos, yPos);
      yPos += 0.15;
  
      doc.setFont("times", "normal");
      doc.text(lines, xPos, yPos);
      yPos += contentHeight + sectionSpacing;
  
      if (inFirstColumn) {
        yOffset = yPos;
      } else {
        yOffsetCol2 = yPos;
      }
    };
  
    drawBorder();
  
    const clientDetails = `
      ${createdAt ? formatDateWithTime(createdAt) : "N/A"}
      ${client.firstName} ${client.lastName}
      ${client.email}
      ${companyName} / ${client.phoneNumber}
    `;
  
    if (imgData) {
      const imgWidthCm = 1.8; 
      const imgHeightCm = 1.8; 
      const imgWidthIn = imgWidthCm / 2.54; 
      const imgHeightIn = imgHeightCm / 2.54; 
  
      const imgX = margin + colWidth - 1.2; 
      const imgY = yOffsetStart; 
  
      doc.addImage(imgData, "PNG", imgX, imgY, imgWidthIn, imgHeightIn);
    }
  
    addSection("Client Information", clientDetails);
  
    const capitalizeFirstLetter = (str) => {
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
  
    const callScoresText = Object.entries(callScores)
      .map(([key, value]) => `- ${capitalizeFirstLetter(key)}: ${value.score} | Notes: ${value.notes || "No notes"}`)
      .join("\n\n");
  
    const redFlagsText = redFlags.length
      ? redFlags.map(flag => `- ${flag.issue} (${flag.timestamp}) | ${flag.context} | Impact: ${flag.impact}`).join("\n\n")
      : "No red flags.";
  
    const greenFlagsText = greenFlags.length
      ? greenFlags.map(flag => `- ${flag.positive_aspect} (${flag.timestamp}) | ${flag.context} | Impact: ${flag.positive_impact}`).join("\n\n")
      : "No green lights.";
  
    const callSummaryText = callSummary || "No call summary available.";
  
    addSection("Call Summary", callSummaryText);
    addSection("Red Flags", redFlagsText);
    addSection("Green Lights", greenFlagsText);
    addSection("Call Scores", callScoresText);
  
    const imageWidth = 1;
    const imageHeight = 0.18;
    const marginRight = 0.5;
    const marginBottom = 0.5;
    const textSpacing = 0.1;
  
    const text = "";
    doc.setFontSize(10);
    const textWidth = doc.getTextWidth(text);
  
    const totalWidth = imageWidth + textSpacing + textWidth;
    const imageX = pageWidth - totalWidth - marginRight;
    const imageY = pageHeight - imageHeight - marginBottom;
  
    doc.addImage("/footer_logo.png", "PNG", imageX, imageY, imageWidth, imageHeight);
  
    const textX = imageX + imageWidth + textSpacing;
    const textY = imageY + imageHeight / 2 + 0.05;
  
    const r = 126;
    const g = 111;
    const b = 138;
  
    doc.setFont("Albert Sans");
    doc.setTextColor(r, g, b);
    doc.text(text, textX, textY);
    doc.setFontSize(16);
  
    doc.save(`${client.firstName}_${client.lastName}_Details.pdf`);
  };

  if (loadingClient) {
    return <div>Loading client data...</div>;
  }

  if (!client) {
    return <div>No client data available.</div>;
  }

  return (
    <div className="client-details-calls">
      <div className="client-header-calls">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <h1>Al Call Score</h1>
          <Button
            variant="outlined"
            onClick={() => navigate(`/client/${clientId}`)}
            sx={{
              borderColor: "#D3C3FC",
              color: "#1F1534",
              padding: "0.3rem 0.8rem",
              fontSize: '0.8rem',
              height: '36px'
            }}
          >
            Back to Client
          </Button>
        </div>
        <div id="call-score-chart" className="retention-chart-small-calls">
          <CircularProgressbarWithChildren
            value={adjustedScore}
            maxValue={10}
            circleRatio={0.5}
            text={`${score !== null && score !== undefined ? score.toFixed(2) : "0.00"}`}
            styles={buildStyles({
              textColor: '#291B3B',
              textSize: '24px',
              pathColor: retentionData.color,
              trailColor: '#EDECFD',
              strokeLinecap: 'butt',
              rotation: 0.75,
            })}
          >
            <RadialSeparatorsCalls
              count={4}
              circleRatio={0.5}
              style={{
                background: "#fff",
                width: "2px",
                height: "10%",
              }}
            />
          </CircularProgressbarWithChildren>
          <div className="retention-label-small-calls">
            <span style={{ color: retentionData.color }}>{retentionData.label}</span>
            <span className="call-score-text">Al Call Score</span>
          </div>
        </div>
      </div>

      <div className="client-name-calls">
        {`${client.firstName} ${client.lastName}`}
        <span className={`status-calls ${getStatus(client.clientRetentionScore).color}`}>
          {getStatus(client.clientRetentionScore).label}
        </span>
      </div>

      {companyName && (
        <div className="client-company-name-calls">
          @{companyName}
        </div>
      )}

      <div className="client-usertag-calls">{client.userTag}</div>

      {!isViewMode && (
        <>
          {showUploadButton && !showSaveButtonOnly && (
            <div className="transcript-actions">
              <UploadTranscript
                setTranscript={setTranscript}
                setTranscriptId={setTranscriptId}
                setIsUploaded={setIsUploaded}
                setShowUploadButton={setShowUploadButton}
              />
            </div>
          )}
          {isUploaded && !showUploadButton && !showSaveButtonOnly && (
            <div className="transcript-actions">
              <Button
                variant="outlined"
                onClick={() => {
                  sendTranscript();
                  setScoreCallClicked(true);
                  setShowProgressBar(true);
                }}
                disabled={loading || isScoreCallDisabled}
                sx={{
                  borderColor: "#D3C3FC",
                  color: "#1F1534",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
              >
                {loading ? "Scoring Call..." : buttonText}
              </Button>
            </div>
          )}
          {showProgressBar && (
            <div style={{ marginTop: "1rem" }}>
              <LinearProgress
                variant="determinate"
                value={loading ? progress : 100}
                sx={{
                  width: "100%",
                  height: "10px",
                  borderRadius: "5px",
                  backgroundColor: "#D3C3FC",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#03BC72",
                  },
                }}
              />
            </div>
          )}
          {callScoreAvailable && showSaveButtonOnly && (
            <div className="transcript-actions">
              <Button
                variant="contained"
                onClick={async () => {
                  await saveCallScore();
                  setIsButtonDisabled(true);
                  setTimeout(() => {
                    setIsExportToPDFEnabled(true);
                  }, 2000);
                }}
                sx={{
                  backgroundColor: "#03BC72",
                  color: "#fff",
                  marginTop: "3rem",
                  marginBottom: "1rem",
                  "&:hover": {
                    backgroundColor: "#02a662",
                  },
                }}
                disabled={isButtonDisabled}
              >
                {isButtonDisabled ? "Call Score Saved Successfully" : "Save Call Score"}
              </Button>
            </div>
          )}
        </>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '35px' }}>
        <h2 className="contact-title-calls" style={{ marginBottom: '25px' }}>
          Contact
        </h2>
      </div>

      <div className="contact-info-calls">
        <div>
          <div className="attribute-calls">
            <span className="attribute-in">Email:</span>{" "}
            <span className="contact-attribute-calls">{client.email}</span>
          </div>
          <div className="attribute-calls">
            <span className="attribute-in">Phone:</span>{" "}
            <span className="contact-attribute-calls">{client.phoneNumber}</span>
          </div>
          <div className="attribute-calls">
            <span className="attribute-in">Address:</span>{" "}
            <span className="contact-attribute">{client.address}</span>
          </div>
        </div>
        <div>
          <div className="attribute-calls">
            <span className="attribute-in">Onboarding:</span>{" "}
            <span className="contact-attribute">
              {new Date(client.onboardingDate).toLocaleDateString('en-US')}
            </span>
          </div>
          <div className="attribute-calls">
            <span className="attribute-in">Office:</span>{" "}
            <span className="contact-attribute">{client.office}</span>
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: '2rem',
        width: '100%'
      }}>
        <div style={{ alignSelf: 'flex-start' }}> 
          {callScoreId && (
            <Button
              variant="contained"
              onClick={() => setOpenDeleteDialog(true)}
              sx={{
                backgroundColor: 'rgba(245,43,83,0.72)',
                color: "#fff",
                padding: "0.3rem 0.8rem",
                fontSize: '0.8rem',
                height: '36px',
                minWidth: '120px',
                "&:hover": {
                  backgroundColor: "#d41f45",
                },
              }}
            >
              Delete Call Review
            </Button>
          )}
        </div>
        
        <div style={{ alignSelf: 'flex-end' }}> 
          <Button
            variant="contained"
            onClick={exportToPDF}
            sx={{
              backgroundColor: "#03BC72",
              color: "#fff",
              padding: "0.3rem 0.8rem",
              fontSize: '0.8rem',
              height: '36px',
              minWidth: '140px',
              "&:hover": {
                backgroundColor: "#02a662",
              },
            }}
            disabled={!isExportToPDFEnabled}
          >
            Export Report As PDF
          </Button>
        </div>
      </div>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this call review? 
            This action cannot be undone. Type "delete" to confirm.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            placeholder="Type 'delete'"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCallReview}
            color="error"
            disabled={deleteConfirmationText.toLowerCase() !== 'delete'}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClientDetailsCalls;